import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Download, ExpandMore, Refresh, Save } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import {
  downloadAuthenticatedPdf,
  openAuthenticatedPdfViaToken
} from '../../utils/authenticatedPdf';

function ReadOnlyField({ label, value, multiline = false }) {
  return (
    <TextField
      fullWidth
      label={label}
      value={value != null && value !== '' ? String(value) : '—'}
      multiline={multiline}
      minRows={multiline ? 2 : 1}
      InputProps={{ readOnly: true }}
      size="small"
      sx={{ '& .MuiInputBase-input': { color: 'text.primary' } }}
    />
  );
}

function TotalsGrid({ totals }) {
  const t = totals || {};
  const pairs = [
    ['Airframe Hours', t.airframeHours],
    ['Landings', t.landings],
    ['Engine 1 Hours', t.engine1Hours],
    ['Engine 2 Hours', t.engine2Hours],
    ['LH CT Cycles', t.lhEngineCyclesCT],
    ['LH PT Cycles', t.lhEngineCyclesPT],
    ['LH IMP Cycles', t.lhEngineCyclesIMP],
    ['RH CT Cycles', t.rhEngineCyclesCT],
    ['RH PT Cycles', t.rhEngineCyclesPT],
    ['RH IMP Cycles', t.rhEngineCyclesIMP],
    ['Disk Wear', t.diskWear],
    ['MGB Torque (TCY)', t.mgbTorqueTcy]
  ];
  return (
    <Grid container spacing={1.5}>
      {pairs.map(([label, val]) => (
        <Grid item xs={12} sm={6} md={4} key={label}>
          <ReadOnlyField label={label} value={val} />
        </Grid>
      ))}
    </Grid>
  );
}

function FuelGrid({ fuel }) {
  const f = fuel || {};
  const pairs = [
    ['Fuel in Tank BF (kg)', f.fuelInTankBF],
    ['Fuel Uplifted (kg)', f.fuelUplifted],
    ['Total Fuel Before Flight (kg)', f.totalFuelBeforeFlight],
    ['Fuel After Flight (kg)', f.fuelAfterFlight],
    ['Fuel Consumption (kg)', f.fuelConsumption],
    ['LH Engine Oil Consumption (ml)', f.lhEngineOilConsumption],
    ['RH Engine Oil Consumption (ml)', f.rhEngineOilConsumption]
  ];
  return (
    <Grid container spacing={1.5}>
      {pairs.map(([label, val]) => (
        <Grid item xs={12} sm={6} md={4} key={label}>
          <ReadOnlyField label={label} value={val} />
        </Grid>
      ))}
    </Grid>
  );
}

const SectorCrsFormDialog = ({
  open,
  onClose,
  entryId,
  sectorSlot,
  onSaved
}) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [savedAtIst, setSavedAtIst] = useState('');
  const [savedByName, setSavedByName] = useState('');

  const baseUrl = entryId && sectorSlot
    ? `/api/technical-log-book/${entryId}/sectors/${sectorSlot}/crs-form`
    : null;

  const loadForm = useCallback(async () => {
    if (!baseUrl) return;
    setLoading(true);
    try {
      const res = await axios.get(baseUrl);
      const snapshot = res.data?.snapshot || {};
      const saved = res.data?.saved;
      setFormData(saved?.data || snapshot);
      setSavedAt(saved?.savedAt || null);
      setSavedAtIst(saved?.savedAtIst || '');
      const by = saved?.savedBy;
      setSavedByName(by ? `${by.firstName || ''} ${by.lastName || ''}`.trim() : '');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load CRS form', 'error');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, showToast]);

  useEffect(() => {
    if (open) loadForm();
  }, [open, loadForm]);

  const handleRefresh = async () => {
    if (!baseUrl) return;
    setLoading(true);
    try {
      const res = await axios.get(baseUrl);
      setFormData(res.data?.snapshot || {});
      showToast('Refreshed from current tech log book data', 'info');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to refresh', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!baseUrl || !formData) return;
    setSaving(true);
    try {
      const res = await axios.post(`${baseUrl}/save`, { data: formData });
      setSavedAt(res.data?.savedAt || new Date().toISOString());
      setSavedAtIst(res.data?.savedAtIst || '');
      const by = res.data?.savedBy;
      setSavedByName(by ? `${by.firstName || ''} ${by.lastName || ''}`.trim() : '');
      if (res.data?.data) setFormData(res.data.data);
      showToast('CRS form saved', 'success');
      onSaved?.({
        data: res.data?.data || formData,
        savedAt: res.data?.savedAt,
        savedAtIst: res.data?.savedAtIst || '',
        savedBy: res.data?.savedBy?._id || res.data?.savedBy || null
      });
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save CRS form', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!baseUrl) return;
    if (!savedAt) {
      showToast('Save the CRS form before downloading PDF', 'warning');
      return;
    }
    setDownloading(true);
    const pdfUrl = `${baseUrl}/pdf?download=1`;
    const fileName = `tlb-crs-sector-${sectorSlot}.pdf`;
    try {
      await downloadAuthenticatedPdf(pdfUrl, fileName);
    } catch (err) {
      try {
        openAuthenticatedPdfViaToken(pdfUrl);
        showToast('Opening CRS PDF in a new tab', 'info');
      } catch (fallbackErr) {
        showToast(err.message || fallbackErr.message || 'Failed to download CRS PDF', 'error');
      }
    } finally {
      setDownloading(false);
    }
  };

  const h = formData?.header || {};
  const defect = formData?.defectReport || {};
  const mel = formData?.mel || {};
  const pilot = formData?.pilotAcceptance || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        CRS Form — Sector {sectorSlot}
        {savedAt && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Last saved: {savedAtIst
              || `${new Date(savedAt).toLocaleString('en-GB', {
                timeZone: 'Asia/Kolkata',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              })} IST`}
            {savedByName ? ` by ${savedByName}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {formData && (
          <Stack spacing={1}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Header</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Sheet Number" value={h.sheetNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Operator" value={h.operatorName} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Aircraft Type" value={h.aircraftType} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Registration" value={h.aircraftRegistration} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Serial Number" value={h.aircraftSerialNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Entry Date" value={h.entryDate} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Work Order Number" value={h.workOrderNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Work Order Date" value={h.workOrderDate} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="CRS Number" value={h.crsNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="CRS Date" value={h.crsDate} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="CRS Value" value={h.crsValue} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AMO Name" value={h.amoName} /></Grid>
                  <Grid item xs={12}><ReadOnlyField label="Maintenance Details" value={h.maintenanceDetails} multiline /></Grid>
                  <Grid item xs={12}><ReadOnlyField label="CRS Text" value={h.crsText} multiline /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Total Brought Forward</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TotalsGrid totals={formData.totalBroughtForward} />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Fuel Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FuelGrid fuel={formData.fuel} />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Defect Report</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}><ReadOnlyField label="Pilot Defect Report" value={defect.pilotDefectReport} multiline /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Pilot CHPL" value={defect.pilotCHPLNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Pilot Defect Time" value={defect.pilotDefectTime} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Pilot Signature" value={defect.pilotSignatureCHPL} /></Grid>
                  <Grid item xs={12}><ReadOnlyField label="AME Defect Report" value={defect.ameDefectReport} multiline /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AME Auth Number" value={defect.ameAuthNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AME Defect Time" value={defect.ameDefectTime} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AME Signature" value={defect.ameSignatureAuth} /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>MEL Release</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="MEL Released" value={mel.melReleased} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AME Auth Number" value={mel.ameAuthNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="AME Signature" value={mel.ameAuthorizedPersonSignature} /></Grid>
                  <Grid item xs={12}><ReadOnlyField label="MEL Section 4 Text" value={mel.melSection4Text} multiline /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Pilot Acceptance</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}><ReadOnlyField label="Acceptance Text" value={pilot.acceptanceText} multiline /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="CHPL Number" value={pilot.chplNumber} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Acceptance Time" value={pilot.acceptanceTime} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Acceptance Date" value={pilot.acceptanceDate} /></Grid>
                  <Grid item xs={12} sm={6}><ReadOnlyField label="Signature / CHPL" value={pilot.signatureCHPLNumber} /></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<Refresh />} onClick={handleRefresh} disabled={loading || saving}>
          Refresh
        </Button>
        <Button
          startIcon={<Download />}
          onClick={handleDownloadPdf}
          disabled={loading || saving || downloading || !savedAt}
        >
          {downloading ? 'Downloading…' : 'Download PDF'}
        </Button>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={loading || saving || !formData}
        >
          {saving ? 'Saving…' : 'Save CRS'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function SectorCrsFormButton({
  entryId,
  sectorSlot,
  sectorKey,
  isReadOnly,
  updateSectorField,
  savedCrsForm
}) {
  const [open, setOpen] = useState(false);

  const handleSaved = (crsForm) => {
    if (updateSectorField && crsForm) {
      updateSectorField(sectorKey, 'bffCertification.crsForm', crsForm);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setOpen(true)}
        disabled={!entryId}
        sx={{ ml: 1 }}
      >
        CRS
        {savedCrsForm?.savedAt ? ' ✓' : ''}
      </Button>
      <SectorCrsFormDialog
        open={open}
        onClose={() => setOpen(false)}
        entryId={entryId}
        sectorSlot={sectorSlot}
        onSaved={handleSaved}
      />
    </>
  );
}
